import {
  CompanyAttributes,
  MaterialAttributes,
  OrderAttributes,
  OrderDetailAttributes,
  SubRouteAttributes,
  TripAttributes,
} from "@albatrosdeveloper/ave-models-package";
import BusinessPartnerAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/business-partners/business-partner/business-partner.entity";
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from "@angular/animations";
import { CommonModule } from "@angular/common";
import { Component, OnDestroy, signal, TemplateRef, ViewChild } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatOptionModule } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatDialog } from "@angular/material/dialog";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatMenuModule } from "@angular/material/menu";
import {
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from "@angular/material/paginator";
import { MatSelectModule } from "@angular/material/select";
import { MatSidenavContainer } from "@angular/material/sidenav";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatSortModule } from "@angular/material/sort";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { MatTabChangeEvent, MatTabsModule } from "@angular/material/tabs";
import { MatTooltipModule } from "@angular/material/tooltip";
import { Router } from "@angular/router";
import { NgSelectModule } from "@ng-select/ng-select";
import { FilterSectionComponent } from "app/common/components/filter-section/filter-section.component";
import { FilterComponent } from "app/common/components/filter/filter.component";
import { IPaginationResult } from "app/common/interfaces";
import { SearchService } from "app/layout/common/search-page/search-page.service";
import { ExportDialogComponent } from "app/layout/export-overlay/dialog/export-dialog.component";
import { ExportService } from "app/layout/export-overlay/export-overlay.service";
import { ExportOptions } from "app/layout/export-overlay/tasks/core/export-task.interfaces";
import { CustomMatPaginatorIntl } from "app/mock-api/common/customPaginatorIntl";
import { MonedaPipe } from "app/pipes/moneda.pipe";
import { FormatOtherDateHourPipe } from "app/pipes/other-date.pipe";
import { getCode } from "app/utils/method";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import {
  debounceTime,
  distinctUntilChanged,
  Subject,
  Subscription,
} from "rxjs";
import { BusinessPartnerService } from "../../business-partners/business-partner/business-partner.service";
import { CompanyService } from "../../configuration/company/company.service.service";
import { BusinessPartnersFiltersComponent } from "../../filters/business-partners-filters/business-partners-filters.component";
import { HoverMenuDirective } from "@fuse/directives/hover-menu/hover-menu.directive";
import { ConfigurationFiltersComponent } from "../../filters/configuration-filters/configuration-filters.component";
import { DatesFiltersComponent } from "../../filters/dates-filters/dates-filters.component";
import { DocumentsFiltersComponent } from "../../filters/documents-filters/documents-filters.component";
import { IFilters } from "../../filters/interfaces/filterInterface";
import { MaterialsManagementFiltersComponent } from "../../filters/materials-management-filters/materials-management-filters.component";
import { RoutesFiltersComponent } from "../../filters/routes-filters/routes-filters.component";
import { StatusFiltersComponent } from "../../filters/status-filters/status-filters.component";
import { MaterialService } from "../../material/material/material.service";
import { SubRouteService } from "../../routing/sub-routes/sub-route.service";
import {
  LOST_SALE_COMMERCIAL_TITLE,
  LOST_SALE_LOGISTIC_TITLE,
  LOST_SALE_REAL_TITLE,
  LOST_SALE_RESUME_TITLE,
} from "./exports/lost-sale.constants";
import { LostSaleExport } from "./exports/lost-sale.export";
import { LostSaleService } from "./lost-sale.service";
import { UserRouteDisplayComponent } from 'app/shared/components/user-route-display/user-route-display.component';
import { DateHelper } from "app/utils/date.helper";
import { DecimalStockCompanyPipe } from "app/pipes/decimal-stock-company.pipe";
import { DocumentNavigationComponent } from "app/common/components/document-navigation/document-navigation.component";
import { SmartGridComponent, ColDef, GridConfig, PageChangedEvent, SortChangedEvent } from 'app/shared/components/smart-grid';
import { TripCellRendererComponent } from "../visits/visit-report/cell-renderers/trip-cell-renderer.component";
import { RouteUserCellRendererComponent } from "../visits/visit-report/cell-renderers/route-user-cell-renderer.component";
import { DateHourCellRendererComponent } from "app/shared/components/smart-grid/cell-renderers/date-hour-cell-renderer/date-hour-cell-renderer.component";
import { MaterialCellRendererComponent } from "app/shared/components/smart-grid/cell-renderers/material-cell-renderer/material-cell-renderer.component";
import { QuantitySolicitedCellRendererComponent } from "../historic/order-customer/cell-renderers/quantity-solicited-cell-rendered-lost.component";
import { QuantitySaleCellRendererComponent } from "../historic/order-customer/cell-renderers/quantity-sale-cell-rendered.component";
import { QuantityMomentCellRendererComponent } from "../historic/order-customer/cell-renderers/quantity-moment-cell-rendered.component";
import { QuantityLostCellRendererComponent } from "../historic/order-customer/cell-renderers/quantity-lost-cell-rendered.component";
import { BusinessPartnerLostSalesCellRendererComponent } from "../historic/order-customer/cell-renderers/business-partner-lost-sale-cell-rendered.component";
import { OrderLostCellRendererComponent } from "../historic/order-customer/cell-renderers/order-lost-sale-cell-rendered.component";
import { QuantityDeliveredLogisticCellRendererComponent } from "../historic/order-customer/cell-renderers/solicited-delivered-logistic-cell-rendered.component";
import { QuantityLostSaleLogisticCellRendererComponent } from "../historic/order-customer/cell-renderers/quantity-lost-sale-logistic-cell-rendered.component";
import { DeliveredDocumentLogisticCellRendererComponent } from "../historic/order-customer/cell-renderers/delivered-document-logistic-cell-rendered.component";
import { InvoiceDocumentLogisticCellRendererComponent } from "../historic/order-customer/cell-renderers/invoice-document-real-cell-rendered.component";
import { QuantityInvoiceRealCellRendererComponent } from "../historic/order-customer/cell-renderers/quantity-invoice-real-cell-rendered.component";
import { LocationResumeCellRendererComponent } from "../historic/order-customer/cell-renderers/location-resume-cell-rendered.component";
import { SumsResumeCellRendererComponent } from "../historic/order-customer/cell-renderers/sums-resume-cell-rendered.component";
import { QuantitySoldResumeCellRendererComponent } from "../historic/order-customer/cell-renderers/quantity-sold-resume-cell-rendered.component";
import { QuantityDeliveredResumeCellRendererComponent } from "../historic/order-customer/cell-renderers/quantity-delivered-resume-cell-rendered.component";
import { ResponsibleResumeCellRendererComponent } from "../historic/order-customer/cell-renderers/responsible-resume-cell-rendered.component";
import { QuantityRealLostCellRendererComponent } from "../historic/order-customer/cell-renderers/quantity-real-lost-sale-cell-rendered.component";
import { SalesOfficeCellRendererComponent } from "../visits/visit-report/cell-renderers/sales-office-cell-renderer.component";

@Component({
  selector: "app-lost-sale",
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSortModule,
    MatPaginatorModule,
    MatIconModule,
    MatSelectModule,
    MatOptionModule,
    MatTooltipModule,
    MatExpansionModule,
    MatSidenavContainer,
    MatMenuModule,
    MatDatepickerModule,
    FormsModule,
    //DateHourPipe,
    MonedaPipe,
    MatTabsModule,
    NgSelectModule,
    NgxMatSelectSearchModule,
    FormatOtherDateHourPipe,
    FilterComponent,
    StatusFiltersComponent,
    MaterialsManagementFiltersComponent,
    DatesFiltersComponent,
    ConfigurationFiltersComponent,
    DocumentsFiltersComponent,
    RoutesFiltersComponent,
    BusinessPartnersFiltersComponent,
    FilterSectionComponent,
    UserRouteDisplayComponent,
    HoverMenuDirective,
    DecimalStockCompanyPipe,
    DocumentNavigationComponent,
    SmartGridComponent
  ],
  providers: [
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
    LostSaleExport,
  ],
  templateUrl: "./lost-sale.component.html",
  styleUrl: "./lost-sale.component.scss",
  animations: [
    trigger("detailExpand", [
      state("collapsed,void", style({ height: "0px", minHeight: "0" })),
      state("expanded", style({ height: "*" })),
      transition(
        "expanded <=> collapsed",
        animate("225ms cubic-bezier(0.4, 0.0, 0.2, 1)")
      ),
    ]),
  ],
})
export class LostSaleComponent implements OnDestroy {
  @ViewChild("rowDetailsTemplate", { static: false })
  rowDetailsTemplate!: TemplateRef<any>;

  getCode = getCode;
  displayedColumnsC: string[] = [
    //"det",
    "trip",
    "routeUser",
    "date",
    "shipToName",
    "serials",
    "materialName",
    "soldQuantitySuggested",
    "soldQuantity",
    "stockAtCreation",
    "lostSale",
  ];
  dataSourceC = new MatTableDataSource<OrderDetailAttributes>([]);

  displayedColumnsL: string[] = [
    "trip",
    "routeUser",
    "date",
    "shipToName",
    "serials",
    "deliveryDocument",
    "materialName",
    "soldQuantitySuggested",
    "deliveredQuantity",
    "lostSale",
  ];
  dataSourceL = new MatTableDataSource<any>([]);

  displayedColumnsE: string[] = [
    "trip",
    "routeUser",
    "date",
    "shipToName",
    "serials",
    "invoiceDocument",
    "materialName",
    "soldQuantitySuggested",
    "invoiceQuantity",
    "lostSale",
  ];
  dataSourceE = new MatTableDataSource<any>([]);

  displayedColumnsR: string[] = [
    "numberOrder",
    "invoiceDocument",
    "sellerToName",
    "shipToName",
    "materialName",
    "locationTwo",
    "sumSoldQuantity",
    "sumStockConfirmedERP",
    "sumDeliveredQuantity",
    "sumInvoiceQuantity",
    "soldQuantity",
    "stockConfirmedERP",
    "deliveredQuantity",
    "invoiceQuantity",
    "lostSale",
  ];
  dataSourceR = new MatTableDataSource<any>([]);

  //COMERCIAL, LOGISTICA, ENTREGA
  totalItems = 0;
  search: string = "";
  pageSize = 50;
  pageIndex = 1;
  sortActive: keyof OrderAttributes = "_id";
  sortDirection: "ASC" | "DESC" = "ASC";

  //RESUMEN
  totalItemsResume = 0;
  searchResume: string = "";
  pageSizeResume = 50;
  pageIndexResume = 1;
  sortActiveResume: keyof OrderAttributes = "_id";
  sortDirectionResume: "ASC" | "DESC" = "ASC";

  listLostSale: { code: string; name: string }[] = [
    { code: "COMERCIAL", name: "Comercial" }, //Pedidos
    { code: "LOGISTICA", name: "Logística" }, //Guia Remisión
    { code: "ENTREGA", name: "Real" }, //Facturación
    { code: "RESUMEN", name: "Resumen" },
  ];
  selectLostSale: any[] = ["COMERCIAL", "RESUMEN"];
  isSelectedResume: boolean = false;

  filter = {
    dateini: new Date(),
    dateend: new Date(),
    company: null,
    routes: [],
    businessPartners: [],
    item: null,
  };

  range = new FormGroup({
    start: new FormControl<Date | null>(new Date()),
    end: new FormControl<Date | null>(new Date()),
  });
  companyList: CompanyAttributes[] = [];
  routeList: SubRouteAttributes[] = [];
  filterRouteList: SubRouteAttributes[] = [];
  searchRoute = "";
  currency: string;

  businessPartnerList: BusinessPartnerAttributes[] = [];
  searchBusiness = "";
  private searchSubjectBusiness = new Subject<string>();

  materialList: MaterialAttributes[] = [];
  searchMaterial = "";
  private searchSubject = new Subject<string>();
  UMPActive: boolean;

  //BUSCADOR
  searchSubscription: Subscription;
  form: FormGroup;
  selectedTabLabel: string = "Venta Perdida Comercial";

  loadFiltersData = false;

  columnDefsCommercial: ColDef<OrderDetailAttributes>[] = [];
  gridConfigCommercial: GridConfig<OrderDetailAttributes> = {} as GridConfig<OrderDetailAttributes>;

  columnDefsLogistic: ColDef<OrderDetailAttributes>[] = [];
  gridConfigLogistic: GridConfig<OrderDetailAttributes> = {} as GridConfig<OrderDetailAttributes>;

  columnDefsReal: ColDef<OrderDetailAttributes>[] = [];
  gridConfigReal: GridConfig<OrderDetailAttributes> = {} as GridConfig<OrderDetailAttributes>;

  columnDefsResume: ColDef<OrderDetailAttributes>[] = [];
  gridConfigResume: GridConfig<OrderDetailAttributes> = {} as GridConfig<OrderDetailAttributes>;
  isLoading = signal<boolean>(false);

  private initializeColumnDefinitionsCommercial(): void {
    this.columnDefsCommercial = [
      {
        field: "trip.id" as keyof OrderDetailAttributes,
        headerName: "Viaje",
        minWidth: 150,
        sortable: true,
        cellRenderer: TripCellRendererComponent,
        cellRendererParams: {
          onTripClick: (data) => this.goToTravel(data),
        },
      },
      {
        colId: "routeUser",
        field: "subRoute.name" as keyof OrderDetailAttributes,
        pinned: "left",
        headerName: "Ruta - Usuario",
        cellRenderer: RouteUserCellRendererComponent,
        minWidth: 300,
        sortable: true,
      },
      //Sales Office Name Added
      {
        field: "salesOffice" as keyof OrderDetailAttributes,
        pinned: "left",
        headerName: "Oficina de Venta",
        cellRenderer: SalesOfficeCellRendererComponent,
        minWidth: 200,
        sortable: true,
        filterOptions: {
        valueExtractor: (item: any) => item?.salesOffice?.name ?? '-',
  }
      },
      {
        field: "date" as keyof OrderDetailAttributes,
        headerName: "Fecha Creación",
        cellRenderer: DateHourCellRendererComponent,
        minWidth: 180,
        sortable: true,
        filterOptions: {
          valueExtractor: (item: any) => {
            if (!item.date) return "-";
            const date = new Date(item.date);
            return date.toLocaleDateString("es-ES");
          },
          customFilterFn: (rowData: any, filterValue: string) => {
            if (!rowData.date) return !filterValue;
            const date = new Date(rowData.date);
            const formattedDate = date.toLocaleDateString("es-ES");
            return formattedDate === filterValue;
          },
        },
      },
      {
        field: "loadDateTime" as keyof OrderDetailAttributes,
        headerName: "Fecha Carga",
        cellRenderer: DateHourCellRendererComponent,
        minWidth: 180,
        sortable: true,
        filterOptions: {
          valueExtractor: (item: any) => {
            if (!item.date) return "-";
            const date = new Date(item.date);
            return date.toLocaleDateString("es-ES");
          },
          customFilterFn: (rowData: any, filterValue: string) => {
            if (!rowData.date) return !filterValue;
            const date = new Date(rowData.date);
            const formattedDate = date.toLocaleDateString("es-ES");
            return formattedDate === filterValue;
          },
        },
      },
      {
        field: "shipToName" as any,
        pinned: "left",
        headerName: "Socio de Negocio",
        minWidth: 150,
        sortable: true,
        cellRenderer: BusinessPartnerLostSalesCellRendererComponent,
      },
      {
        field: "serials" as any,
        pinned: "left",
        headerName: "Pedido",
        minWidth: 150,
        sortable: true,
        cellRenderer: OrderLostCellRendererComponent,
      },
      {
        field: "material.name" as any,
        colId: "material",
        headerName: "Material",
        flex: 2,
        minWidth: 200,
        sortable: true,
        filterable: true,
        cellRenderer: MaterialCellRendererComponent,
      },

      {
        field: "baseQuantity",
        headerName: "Cantidad Solicitada",
        cellRenderer: QuantitySolicitedCellRendererComponent,
        cellRendererParams: {
          getUMPActive: () => this.UMPActive,
        },
        minWidth: 200,
        sortable: false,
      },
      {
        field: "stockConfirmedERP",
        headerName: "Cantidad Vendida",
        cellRenderer: QuantitySaleCellRendererComponent,
        cellRendererParams: {
          getUMPActive: () => this.UMPActive,
        },
        minWidth: 200,
        sortable: false,
      },
      {
        field: "baseStockAtCreation",
        headerName: "Stock al momento",
        cellRenderer: QuantityMomentCellRendererComponent,
        cellRendererParams: {
          getUMPActive: () => this.UMPActive,
        },
        minWidth: 200,
        sortable: false,
      },
      {
        field: "baseLostSale" as any,
        headerName: "Venta Perdida",
        cellRenderer: QuantityLostCellRendererComponent,
        cellRendererParams: {
          getUMPActive: () => this.UMPActive,
        },
        minWidth: 200,
        sortable: false,
      },
    ];
  }

    private initializeGridConfigCommercial(): void {
     this.gridConfigCommercial = {
       rowIdField: "_id" as any,
       emptyState: {
         message: "No encontramos tus resultados!",
         showIcon: true,
       },
       pagination: {
         enabled: true,
         mode: "api",
         pageSize: 50,
         pageSizeOptions: [50, 100, 150, 200],
         showPaginator: true,
       },
       sorting: {
         enabled: true,
         mode: "api",
         multiColumn: false,
         defaultSort: [{ field: "_id" as any, direction: "desc" }],
       },
       search: {
         enabled: false,
       },
       columnFilters: {
         enabled: true,
         mode: "local",
       },
       columnVisibility: {
         enabled: false, // Disabled - now in settings panel
       },
       selection: {
         enabled: false,
       },
       autoSizing: {
         enabled: true,
         includeHeaders: true,
         skipHidden: true,
         padding: 40, // Increased for more spacing
       },
       styling: {
         maxHeight: "85vh",
         minHeight: "80vh",
         density: "comfortable",
         fontSize: "12px",
         cellPaddingX: "3px",
         cellPaddingY: "3px",
       },
       settingsPanel: {
         enabled: true,
         position: "right",
         enableColumnVisibility: true,
         defaultDensity: "compact",
       },
     };
  }

  private initializeColumnDefinitionsLogistic(): void {
    this.columnDefsLogistic = [
        { field: 'trip.id' as any, headerName: 'Viaje', minWidth: 150, cellRenderer: TripCellRendererComponent, cellRendererParams: { onTripClick: (data: any) => this.goToTravel(data) } },
        { field: 'subRoute.name' as keyof OrderDetailAttributes, headerName: 'Ruta - Usuario', cellRenderer: RouteUserCellRendererComponent, minWidth: 250 },

        { field: 'salesOffice' as keyof OrderDetailAttributes, headerName: 'Oficina de Venta', cellRenderer: SalesOfficeCellRendererComponent, minWidth: 200, filterOptions: { valueExtractor: (item: any) => item?.salesOffice?.name ?? '-', } },

        { field: 'date' as keyof OrderDetailAttributes, headerName: 'Fecha Creación', cellRenderer: DateHourCellRendererComponent, minWidth: 180 },
        { field: 'loadDateTime' as keyof OrderDetailAttributes, headerName: 'Fecha Carga', cellRenderer: DateHourCellRendererComponent, minWidth: 180 },
        { field: 'shipToName' as keyof OrderDetailAttributes, headerName: 'Socio de Negocio', minWidth: 200, cellRenderer: BusinessPartnerLostSalesCellRendererComponent },
        { field: 'serials' as keyof OrderDetailAttributes, headerName: 'Pedido', minWidth: 150, cellRenderer: OrderLostCellRendererComponent },
        {
            field: 'deliveryDocument.serial' as keyof OrderDetailAttributes,
            headerName: 'Entrega',
            cellRenderer: DeliveredDocumentLogisticCellRendererComponent
        },
        { field: 'material.name' as keyof OrderDetailAttributes, headerName: 'Material', minWidth: 200, cellRenderer: MaterialCellRendererComponent },
        { field: 'baseQuantitySuggested' as keyof OrderDetailAttributes, headerName: 'Cantidad Solicitada', cellRenderer: QuantitySolicitedCellRendererComponent, cellRendererParams: { getUMPActive: () => this.UMPActive } },
        { field: 'baseQuantityReceivedByERP' as keyof OrderDetailAttributes, headerName: 'Cantidad Atendida', cellRenderer: QuantityDeliveredLogisticCellRendererComponent, cellRendererParams: { getUMPActive: () => this.UMPActive } }, // Reutiliza lógica de UMB/UMP
        { field: 'baseLogisticLostSale' as keyof OrderDetailAttributes, headerName: 'Venta Perdida', cellRenderer: QuantityLostSaleLogisticCellRendererComponent, cellRendererParams: { getUMPActive: () => this.UMPActive } }
    ];
  }

  private initializeColumnDefinitionsReal(): void {
      this.columnDefsReal = [
          ...this.columnDefsLogistic.slice(0, 5),
          { field: 'serials' as keyof OrderDetailAttributes, headerName: 'Pedido', minWidth: 150, cellRenderer: OrderLostCellRendererComponent },
          {
              field: 'invoiceDocument.serial' as keyof OrderDetailAttributes,
              headerName: 'Factura',
              cellRenderer: InvoiceDocumentLogisticCellRendererComponent
          },
          { field: 'material.name' as keyof OrderDetailAttributes, headerName: 'Material', minWidth: 200, cellRenderer: MaterialCellRendererComponent },
          { field: 'baseQuantitySuggested' as keyof OrderDetailAttributes, headerName: 'Cantidad Solicitada', cellRenderer: QuantitySolicitedCellRendererComponent, cellRendererParams: { getUMPActive: () => this.UMPActive } },
          { field: 'invoiceBaseQuantity' as keyof OrderDetailAttributes, headerName: 'Cantidad Facturada', cellRenderer: QuantityInvoiceRealCellRendererComponent, cellRendererParams: { getUMPActive: () => this.UMPActive } },
          { field: 'baseRealLostSale' as keyof OrderDetailAttributes, headerName: 'Venta Perdida', cellRenderer: QuantityRealLostCellRendererComponent, cellRendererParams: { getUMPActive: () => this.UMPActive } }
      ];
  }

  private initializeColumnDefinitionsResume(): void {
      this.columnDefsResume = [
          { field: 'trip.id' as any, headerName: 'Viaje', minWidth: 150, cellRenderer: TripCellRendererComponent, cellRendererParams: { onTripClick: (data: any) => this.goToTravel(data) } },
          { field: 'subRoute.name' as keyof OrderDetailAttributes, headerName: 'Ruta - Usuario', cellRenderer: RouteUserCellRendererComponent, minWidth: 250 },

          { field: 'salesOffice' as keyof OrderDetailAttributes, headerName: 'Oficina de Venta', cellRenderer: SalesOfficeCellRendererComponent, minWidth: 200, filterOptions: { valueExtractor: (item: any) => item?.salesOffice?.name ?? '-', } },

          { field: 'date' as keyof OrderDetailAttributes, headerName: 'Fecha Creación', cellRenderer: DateHourCellRendererComponent, minWidth: 180 },
          { field: 'loadDateTime' as keyof OrderDetailAttributes, headerName: 'Fecha Carga', cellRenderer: DateHourCellRendererComponent, minWidth: 180 },
          { field: 'shipToName' as keyof OrderDetailAttributes, headerName: 'Socio de Negocio', cellRenderer: BusinessPartnerLostSalesCellRendererComponent },
          { field: 'serials' as keyof OrderDetailAttributes, headerName: 'Pedido', cellRenderer: OrderLostCellRendererComponent },
          { field: 'invoiceDocument.serial' as keyof OrderDetailAttributes, headerName: 'Factura', cellRenderer: InvoiceDocumentLogisticCellRendererComponent },
          { field: 'materialName.name' as keyof OrderDetailAttributes, headerName: 'Material', cellRenderer: MaterialCellRendererComponent },
          { field: 'locationTwo' as keyof OrderDetailAttributes, headerName: 'Ubicación', cellRenderer: LocationResumeCellRendererComponent },
          { field: 'sumSoldQuantity' as keyof OrderDetailAttributes, headerName: `Suma Pedido (${this.currency ?? 'L'})`, cellRenderer: SumsResumeCellRendererComponent, cellRendererParams: { sum: 'sumSoldQuantity' } },
          { field: 'sumStockConfirmedERP' as keyof OrderDetailAttributes, headerName: `Suma Reserva (${this.currency ?? 'L'})`, cellRenderer: SumsResumeCellRendererComponent, cellRendererParams: { sum: 'sumStockConfirmedERP' } },
          { field: 'sumDeliveredQuantity' as keyof OrderDetailAttributes, headerName: `Suma Entrega. (${this.currency ?? 'L'})`, cellRenderer: SumsResumeCellRendererComponent, cellRendererParams: { sum: 'sumDeliveredQuantity' } },
          { field: 'sumInvoiceQuantity' as keyof OrderDetailAttributes, headerName: `Suma Factura (${this.currency ?? 'L'})`, cellRenderer: SumsResumeCellRendererComponent, cellRendererParams: { sum: 'sumInvoiceQuantity' } },
          { field: 'soldQuantity' as keyof OrderDetailAttributes, headerName: 'Cnt. Pedido', cellRenderer: QuantitySolicitedCellRendererComponent, cellRendererParams: { getUMPActive: () => this.UMPActive, field: 'soldQuantity' } },
          { field: 'stockConfirmedERP' as keyof OrderDetailAttributes, headerName: 'Cnt. Reserva', cellRenderer: QuantitySaleCellRendererComponent, cellRendererParams: { getUMPActive: () => this.UMPActive, field: 'stockConfirmedERP' } },
          { field: 'deliveredQuantity' as keyof OrderDetailAttributes, headerName: 'Cnt. Entrega.', cellRenderer: QuantityDeliveredLogisticCellRendererComponent, cellRendererParams: { getUMPActive: () => this.UMPActive } },
          { field: 'invoiceBaseQuantity' as keyof OrderDetailAttributes, headerName: 'Cnt. Fact.', cellRenderer: QuantityInvoiceRealCellRendererComponent, cellRendererParams: { getUMPActive: () => this.UMPActive, field: 'invoiceQuantity' } },
          { field: 'lostSale' as keyof OrderDetailAttributes, headerName: `Dif. (${this.currency ?? 'L'})`, cellRenderer: SumsResumeCellRendererComponent, cellRendererParams: { sum: 'lostSale' } }
      ];
  }

  private initializeGridConfigLogistic() { this.gridConfigLogistic = { ...this.gridConfigCommercial }; }
  private initializeGridConfigReal() { this.gridConfigReal = { ...this.gridConfigCommercial }; }
  private initializeGridConfigResume() {
      this.gridConfigResume = { ...this.gridConfigCommercial };
      this.gridConfigResume.pagination.pageSize = 50;
  }

  private formatMoneda(value: any): string {
      return value ? new Intl.NumberFormat('es-PE', { style: 'currency', currency: this.currency || 'USD' }).format(value) : '-';
  }

  onPageChangeCommercial(event: PageChangedEvent): void {
      this.pageIndex = event.pageIndex + 1;
      this.pageSize = event.pageSize;
      this.getList();
    }

    onSortChangeCommercial(event: SortChangedEvent<OrderDetailAttributes>): void {
      this.pageIndex = 1;
      this.getList();
    }

  constructor(
    private lostSaleService: LostSaleService,
    private readonly companyService: CompanyService,
    private readonly subRouteService: SubRouteService,
    private readonly businessPartnerService: BusinessPartnerService,
    private readonly materialService: MaterialService,
    private readonly searchService: SearchService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    public exportService: ExportService,
    private lostSaleExport: LostSaleExport,
    private readonly router: Router,
    private fb: FormBuilder
  ) {
    const today = new Date();
    this.form = this.fb.group({
      start: [today],
      end: [today],
      companyId: [],
      salesOfficeId: [],
      subRouteId: [],
      assignedUserId: [],
      warehouseId: [],
      materialGroupId: [[]],
      materialId: [],
      customerId: [],
      searchAssignedUser: [],
      pickerUpload_start: [],
      pickerUpload_end: []
    });
  }

  ngOnInit(): void {
    this.initializeColumnDefinitionsCommercial();
    this.initializeGridConfigCommercial();

    this.initializeColumnDefinitionsLogistic();
    this.initializeGridConfigLogistic();

    this.initializeColumnDefinitionsReal();
    this.initializeGridConfigReal();

    this.initializeColumnDefinitionsResume();
    this.initializeGridConfigResume();
  }

  onFilterPanelOpened(): void {
    this.loadFiltersData = true;
  }


  ngAfterViewInit(): void {
    this.searchService.clear();
    this.onHandleSearch();
    this.getCompanies();
    // this.getRoutes();

    this.setupBusinessSearchControl();
    // this.getAllBusinessPartner();

    this.setupMaterialSearchControl();
    // this.getMaterialList();
  }

  public onHandleSearch() {
    this.searchSubscription = this.searchService.searchTerm$.subscribe(
      (term) => {
        !this.isSelectedResume
          ? this.applyFilter(term)
          : this.applyFilterResume(term);
      }
    );
  }

  bomData(element): any {
    return element.bom;
  }

  onFilter() {
    // Apply filters - validate and execute API calls
    // if (this.range.invalid || !this.filter.company) {
    //   this.snackBar.open("Filtro inválido", "Cerrar", { duration: 3000 });
    //   return;
    // }

    // Update filter dates from range control
    this.range.valueChanges.subscribe((rangeValues) => {
      this.filter.dateini = rangeValues.start || this.filter.dateini;
      this.filter.dateend = rangeValues.end || this.filter.dateend;
    });

    // Execute API calls
    this.executeDataLoad();
  }

  private executeDataLoad(): void {
    this.getList();
  }

  clearFilters(): void {
    this.filter = {
      dateini: new Date(),
      dateend: new Date(),
      company: this.filter.company,
      routes: [],
      businessPartners: [],
      item: null,
    };
    this.executeDataLoad();
  }

  //E: Venta Perdida real
  //L: Venta Perdida Logística
  //R: Resumen
  //C: Venta Perdida Comercial
  hasData(): boolean {
    return (
      this.dataSourceR?.data?.length > 0 ||
      this.dataSourceC?.data?.length > 0 ||
      this.dataSourceL?.data?.length > 0 ||
      this.dataSourceE?.data?.length > 0
    );
  }

  getList() {
    let options = {
      currentPage: this.pageIndex,
      limitPerPage: this.pageSize,
      search: this.search,
      sort: this.getSortField(),
      sortDirection: this.sortDirection,
    };

    let filter: IFilters = {
      start: DateHelper.getStartOfDay(this.form.get("start")?.value),
      end: DateHelper.getEndOfDay(this.form.get("end")?.value) ?? DateHelper.getEndOfDay(this.form.get("start")?.value) ?? null,
      pickerUpload_start: DateHelper.getStartOfDay(this.form.get("pickerUpload_start")?.value),
      pickerUpload_end: DateHelper.getEndOfDay(this.form.get("pickerUpload_end")?.value) ?? DateHelper.getEndOfDay(this.form.get("pickerUpload_start")?.value) ?? null,
      companyId: this.form.get("companyId")?.value,
      subRouteId: this.form.get("subRouteId")?.value, //

      salesOfficeId: this.form.get("salesOfficeId")?.value,

      seller: this.form.get("assignedUserId")?.value,
      customerId: this.form.get("customerId")?.value,
      materialId: this.form.get("materialId").value,
      materialGroupId: this.form.get("materialGroupId").value,
      ordersToFilter: null,
    };

    this.lostSaleService.findAllWithCount(options, filter).subscribe({
      next: (res: IPaginationResult<OrderDetailAttributes>) => {
        const data = res?.data?.[0]?.data ?? [];

        this.dataSourceC.data = data;
        this.dataSourceL.data = data;
        this.dataSourceE.data = data;
        this.dataSourceR.data = data;

        data.forEach((record, index) => {
          const deliveryDocument =
            record.relatedDocuments?.find((doc) => doc.type === "DELIVERY") ||
            null;
          const invoiceDocument =
            record.relatedDocuments?.find((doc) => doc.type === "INVOICE") ||
            null;

          if (deliveryDocument) {
            this.dataSourceL.data[index].deliveryDocument = deliveryDocument;
          }

          if (invoiceDocument) {
            this.dataSourceE.data[index].invoiceDocument = invoiceDocument;
          }
        });

        this.totalItems = res?.data?.[0]?.pageInfo?.[0]?.total ?? 0;
        this.totalItemsResume = res?.data?.[0]?.pageInfo?.[0]?.total ?? 0;
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || "Error al obtener reporte",
          "Cerrar",
          { duration: 5000 }
        );
      },
    });
  }

  getListResume() {
    let options = {
      currentPage: this.pageIndexResume,
      limitPerPage: this.pageSizeResume,
      search: this.searchResume,
      sort: this.getSortFieldResume(),
      sortDirection: this.sortDirectionResume,
    };

    let filter: IFilters = {
      start: DateHelper.getStartOfDay(this.form.get("start")?.value),
      end: DateHelper.getEndOfDay(this.form.get("end")?.value) ?? DateHelper.getEndOfDay(this.form.get("start")?.value) ?? null,
      pickerUpload_start: DateHelper.getStartOfDay(this.form.get("pickerUpload_start")?.value),
      pickerUpload_end: DateHelper.getEndOfDay(this.form.get("pickerUpload_end")?.value) ?? DateHelper.getEndOfDay(this.form.get("pickerUpload_start")?.value) ?? null,
      companyId: this.form.get("companyId")?.value,
      subRouteId: this.form.get("subRouteId")?.value, //

      salesOfficeId: this.form.get("salesOfficeId")?.value,

      seller: this.form.get("assignedUserId")?.value,
      customerId: this.form.get("customerId")?.value,
      materialId: this.form.get("materialId").value,
      materialGroupId: this.form.get("materialGroupId").value,
      ordersToFilter: null,
    };

    this.lostSaleService.findAllResumeWithCount(options, filter).subscribe({
      next: (res: IPaginationResult<OrderDetailAttributes>) => {
        const data = res?.data?.[0]?.data ?? [];

        this.dataSourceR.data = data;
        data.forEach((record, index) => {
          const invoiceDocument =
            record.relatedDocuments?.find((doc) => doc.type === "INVOICE") ||
            null;

          if (invoiceDocument) {
            this.dataSourceR.data[index].invoiceDocument = invoiceDocument;
          }
        });

        this.totalItemsResume = res?.data?.[0]?.pageInfo?.[0]?.total ?? 0;
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || "Error al obtener reporte",
          "Cerrar",
          { duration: 5000 }
        );
      },
    });
  }

  //TAB2
  onTabChange(event: MatTabChangeEvent) {
    this.selectedTabLabel = event.tab.textLabel;
  }

  private getSortField(): keyof OrderDetailAttributes {
    const sortFieldMapping: Record<string, keyof OrderDetailAttributes> = {
      trip: <any>"trip.id",
      subRoute: <any>"subRoute.name",
      user: <any>"user",
      serials: <any>"serials",
      date: <any>"date",

      salesOffice: <any>"salesOffice.name",

      shipToExternalKeyCode: <any>"shipToExternalKeyCode",
      shipToName: <any>"shipToName",
      materialName: <any>"materialName.name",
      soldQuantitySuggested: <any>"soldQuantitySuggested",
      invoiceQuantity: <any>"invoiceQuantity",
      lostSale: <any>"lostSale",
    };
    return sortFieldMapping[this.sortActive] || "_id";
  }

  applyFilter(filterValue: string) {
    this.pageIndex = 1;
    this.search = filterValue;
    this.getList();
  }

  changeSort(e) {
    this.sortActive = e.active;
    this.sortDirection = e.direction?.toUpperCase() || "ASC";
    this.getList();
  }

  changePage(e: PageEvent) {
    this.pageIndex = e.pageIndex + 1;
    this.pageSize = e.pageSize;
    this.getList();
  }

  //PAGINATION RESUMEN
  private getSortFieldResume(): keyof OrderDetailAttributes {
    const sortFieldMapping: Record<string, keyof OrderDetailAttributes> = {
      numberOrder: <any>"numberOrder",
      serials: <any>"serials",
      sellerToName: <any>"sellerToName",
      shipToExternalKeyCode: <any>"shipToExternalKeyCode",

      salesOffice: <any>"salesOffice.name",

      shipToName: <any>"shipToName",
      locationTwo: <any>"locationTwo.name",
      sumSoldQuantity: <any>"sumSoldQuantity",
      sumStockConfirmedERP: <any>"sumStockConfirmedERP",
      sumDeliveredQuantity: <any>"sumDeliveredQuantity",
      sumInvoiceQuantity: <any>"sumInvoiceQuantity",
      soldQuantity: "soldQuantity",
      stockConfirmedERP: <any>"stockConfirmedERP",
      deliveredQuantity: <any>"deliveredQuantity",
      invoiceQuantity: <any>"invoiceQuantity",
      lostSale: <any>"lostSale",
    };
    return sortFieldMapping[this.sortActiveResume] || "soldQuantitySuggested";
  }

  applyFilterResume(filterValue: string) {
    this.pageIndexResume = 1;
    this.search = filterValue;
    this.getList();
  }

  changeSortResume(e) {
    this.sortActiveResume = e.active;
    this.sortDirectionResume = e.direction?.toUpperCase() || "ASC";
    this.getList();
  }

  changePageResume(e: PageEvent) {
    this.pageIndexResume = e.pageIndex + 1;
    this.pageSizeResume = e.pageSize;
    this.getList();
  }

  //filter
  private getCompanies(): void {
    this.companyService.findToComboBox().subscribe((res) => {
      this.companyList = res.data;
      this.currency = this.companyList[0].currencyBase.code;
      this.UMPActive = res.data[0].UMPActive;
      if (this.companyList.length == 1) {
        this.filter.company = this.companyList[0].id;
        this.executeDataLoad();
      }
    });
  }

  private getRoutes(): void {
    this.subRouteService.findToComboBox().subscribe((res) => {
      this.routeList = res.data;
      this.filterRouteList = this.routeList.slice();
    });
  }

  changueOptionsRoute(event) {
    if (!this.routeList) {
      return;
    }
    let search = event;
    if (!search) {
      this.filterRouteList = this.routeList.slice();
      return;
    } else {
      search = search.toLowerCase();
    }
    this.filterRouteList = this.routeList.filter(
      (item) =>
        item.externalKeys?.code.toLowerCase().indexOf(search) > -1 ||
        item.name.toLowerCase().indexOf(search) > -1
    );
  }

  private getAllBusinessPartner(): void {
    const materialOptions = {
      search: this.searchBusiness ? this.searchBusiness : null,
      currentPage: 1,
      limitPerPage: 50,
      sortActive: "name",
      //sortDirection: "ASC"
    };

    this.businessPartnerService
      .findAllWithCount(materialOptions, false, [], "both", null)
      .subscribe({
        next: (res: IPaginationResult<BusinessPartnerAttributes>) => {
          this.businessPartnerList = res?.data?.[0]?.data ?? [];
        },
        error: (err) => {
          this.snackBar.open(
            err.error.message || "Error al traer registros",
            "Cerrar",
            { duration: 3000 }
          );
        },
      });
  }

  changueOptionsBusiness(event) {
    this.searchSubjectBusiness.next(event);
  }

  private setupBusinessSearchControl(): void {
    this.searchSubjectBusiness
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((searchTerm) => {
        if (searchTerm) {
          this.searchBusiness = searchTerm;
          this.getAllBusinessPartner();
        }
      });
  }

  private getMaterialList(): void {
    const materialOptions = {
      search: this.searchMaterial ? this.searchMaterial : null,
      currentPage: 1,
      limitPerPage: 50,
      sortActive: "name",
      //sortDirection: "ASC"
    };

    this.materialService
      .findAllWithCount(
        materialOptions,
        this.filter.company ? this.filter.company : []
      )
      .subscribe({
        next: (res: IPaginationResult<MaterialAttributes>) => {
          this.materialList = res?.data?.[0]?.data ?? [];
        },
        error: (err) => {
          this.snackBar.open(
            err.error.message || "Error al traer registros",
            "Cerrar",
            { duration: 3000 }
          );
        },
      });
  }

  changueOptionsMaterial(event) {
    this.searchSubject.next(event);
  }

  private setupMaterialSearchControl(): void {
    this.searchSubject
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((searchTerm) => {
        if (searchTerm) {
          this.searchMaterial = searchTerm;
          this.getMaterialList();
        }
      });
  }

  onExportResumeButtonPressed() {
    if (!this.lostSaleExport.canStartTask(this.selectedTabLabel)) return;
    const dialogRef = this.dialog.open(ExportDialogComponent, {
      data: {
        title:
          this.selectedTabLabel === "Resumen"
            ? LOST_SALE_RESUME_TITLE
            : this.selectedTabLabel === "Venta Perdida Logística"
              ? LOST_SALE_LOGISTIC_TITLE
              : this.selectedTabLabel === "Venta Perdida Real"
                ? LOST_SALE_REAL_TITLE
                : LOST_SALE_COMMERCIAL_TITLE,
      },
    });

    let optionsFilter = {
      currentPage: this.pageIndex,
      limitPerPage: this.pageSize,
      search: this.search,
      sort: this.getSortField(),
      sortDirection: this.sortDirection,
    };

    let filter: IFilters = {
      start: DateHelper.getStartOfDay(this.form.get("start")?.value),
      end: DateHelper.getEndOfDay(this.form.get("end")?.value) ?? DateHelper.getEndOfDay(this.form.get("start")?.value) ?? null,
      pickerUpload_start: DateHelper.getStartOfDay(this.form.get("pickerUpload_start")?.value),
      pickerUpload_end: DateHelper.getEndOfDay(this.form.get("pickerUpload_end")?.value) ?? DateHelper.getEndOfDay(this.form.get("pickerUpload_start")?.value) ?? null,
      companyId: this.form.get("companyId")?.value,
      subRouteId: this.form.get("subRouteId")?.value, //
      seller: this.form.get("assignedUserId")?.value,
      customerId: this.form.get("customerId")?.value,
      materialId: this.form.get("materialId").value,
      materialGroupId: this.form.get("materialGroupId").value,
      ordersToFilter: null,
    };

    dialogRef.afterClosed().subscribe((options: ExportOptions) => {
      if (options) {
        this.lostSaleExport.export(
          options.format,
          options.range,
          this.totalItems,
          this.pageSize,
          this.pageIndex,
          this.selectedTabLabel,
          optionsFilter,
          filter
        );
      }
    });
  }

  goToTravel(element: any) {
    if (element.trip?.id) {
      const queryParams = { tripId: element.trip.id };
      const urlTree = this.router.createUrlTree(
        ["monitor", "principal-screen"],
        { queryParams }
      );
      const absoluteUrl =
        window.location.origin + this.router.serializeUrl(urlTree);
      window.open(absoluteUrl, "_blank");
    }
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }

  onClearFilter() {
    this.form.reset({
      start: new Date(),
      end: new Date(),
    });
    this.executeDataLoad();
  }

   hasTrip(element: OrderAttributes): boolean {
    return !!element?.trip?.id;
  }
}
